"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import {
  HiPhotograph,
  HiUpload,
  HiDownload,
  HiRefresh,
  HiQuestionMarkCircle,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

interface Preset {
  label: string;
  platform: string;
  width: number;
  height: number;
}

const PRESETS: Preset[] = [
  { label: "Post (Square)", platform: "Instagram", width: 1080, height: 1080 },
  { label: "Story / Reel", platform: "Instagram", width: 1080, height: 1920 },
  { label: "Header", platform: "Twitter / X", width: 1500, height: 500 },
  { label: "Cover Photo", platform: "Facebook", width: 820, height: 312 },
  { label: "Banner", platform: "LinkedIn", width: 1584, height: 396 },
  { label: "Thumbnail", platform: "YouTube", width: 1280, height: 720 },
  { label: "Video / Story", platform: "TikTok", width: 1080, height: 1920 },
];

const PLATFORM_COLORS: Record<string, string> = {
  Instagram: "bg-pink-500/10 text-pink-600",
  "Twitter / X": "bg-sky-500/10 text-sky-600",
  Facebook: "bg-blue-600/10 text-blue-600",
  LinkedIn: "bg-blue-700/10 text-blue-700",
  YouTube: "bg-red-500/10 text-red-600",
  TikTok: "bg-slate-500/10 text-slate-600",
};

const FAQ_ITEMS = [
  {
    question: "Does resizing preserve my image quality?",
    answer:
      "The resizer uses the browser's built-in Canvas API with high-quality image smoothing enabled. Downscaling (reducing size) generally preserves quality well. Upscaling from a very small source image may result in pixelation — always start with the highest resolution original you have.",
  },
  {
    question: "Are my images uploaded to your server?",
    answer:
      "No. Everything happens entirely in your browser using the Canvas API. Your image never leaves your device. No data is transmitted to any server.",
  },
  {
    question: "What file formats can I upload?",
    answer:
      "You can upload PNG and JPEG images. The output is always downloaded as a PNG file to preserve quality and support transparency where applicable.",
  },
  {
    question: "Why does the resized image look stretched?",
    answer:
      "Social media platforms have specific aspect ratios that differ from your source image. The tool stretches to fit the exact dimensions of the chosen preset. If you want to avoid stretching, crop your source image to the correct aspect ratio first before uploading.",
  },
  {
    question: "Which preset should I use for Instagram feed posts?",
    answer:
      "Use the Instagram Post (Square) preset at 1080x1080 pixels for square feed posts. Instagram also supports portrait (1080x1350) and landscape (1080x566) posts, but the square format is the most universal for feed grids.",
  },
];

export default function SocialMediaImageResizerClient() {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [resizedDataUrl, setResizedDataUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSourceImage(e.target?.result as string);
      setResizedDataUrl(null);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleResize = async () => {
    if (!sourceImage || !selectedPreset) return;
    setIsProcessing(true);

    const img = new Image();
    img.src = sourceImage;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });

    const canvas = document.createElement("canvas");
    canvas.width = selectedPreset.width;
    canvas.height = selectedPreset.height;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, selectedPreset.width, selectedPreset.height);
      setResizedDataUrl(canvas.toDataURL("image/png"));
    }
    setIsProcessing(false);
  };

  const handleDownload = () => {
    if (!resizedDataUrl || !selectedPreset) return;
    const a = document.createElement("a");
    a.href = resizedDataUrl;
    a.download = `${selectedPreset.platform.toLowerCase().replace(/\s*\/\s*/g, "-")}-${selectedPreset.label.toLowerCase().replace(/\s+/g, "-")}-${selectedPreset.width}x${selectedPreset.height}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setSourceImage(null);
    setSelectedPreset(null);
    setResizedDataUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Social Media Image Resizer"
        description="Resize images to exact social media dimensions for Instagram, Twitter, Facebook, LinkedIn, YouTube, and TikTok. Free browser-based tool."
        url="https://plugmonkey.xyz/tool/social-media-image-resizer/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Social Media Image Resizer",
            url: "https://plugmonkey.xyz/tool/social-media-image-resizer/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      {/* Hero */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiPhotograph className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Social Media Image Resizer</h1>
          <p className="text-muted-foreground mt-1">
            Instantly resize images to the correct dimensions for any platform.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Every social media platform has its own image size requirements. Using the wrong
          dimensions results in cropped thumbnails, blurry covers, or misaligned layouts. This tool
          lets you upload any image and resize it to the exact pixel dimensions required by
          Instagram, Twitter, Facebook, LinkedIn, YouTube, or TikTok.
        </p>
        <p>
          Everything processes in your browser — no uploads, no accounts, no waiting. Pick a preset,
          resize, and download your image in seconds.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiUpload /> Upload Image
              </CardTitle>
              <CardDescription>
                PNG or JPEG, any size. Processed entirely in your browser.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  isDragging ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                }}
                aria-label="Upload image to resize"
              >
                <input
                  id="social-image"
                  name="image"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png, image/jpeg"
                  className="hidden"
                  aria-label="Image file input"
                />
                {sourceImage ? (
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={sourceImage}
                      alt="Uploaded source image"
                      className="max-h-40 max-w-full object-contain rounded-lg shadow-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <HiRefresh className="mr-1 h-4 w-4" /> Change Image
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-muted-foreground">
                    <HiUpload className="w-12 h-12 mb-3 opacity-40" />
                    <p className="font-medium">Click to upload or drag and drop</p>
                    <p className="text-sm opacity-60 mt-1">PNG or JPEG</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preset selector */}
          <Card>
            <CardHeader>
              <CardTitle>Select Platform Preset</CardTitle>
              <CardDescription>
                Choose the platform and content type you are creating for.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PRESETS.map((preset) => (
                  <button
                    key={`${preset.platform}-${preset.label}`}
                    onClick={() => {
                      setSelectedPreset(preset);
                      setResizedDataUrl(null);
                    }}
                    className={`rounded-xl border p-3 text-left transition-all ${
                      selectedPreset?.label === preset.label &&
                      selectedPreset?.platform === preset.platform
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/40"
                    }`}
                    aria-pressed={
                      selectedPreset?.label === preset.label &&
                      selectedPreset?.platform === preset.platform
                    }
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm">{preset.label}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLATFORM_COLORS[preset.platform]}`}
                      >
                        {preset.platform}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">
                      {preset.width} x {preset.height} px
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-primary/5 border-primary/20 shadow-lg">
              <div className="h-1 bg-gradient-to-r from-primary to-orange-500 rounded-t-lg" />
              <CardHeader>
                <CardTitle>Resize &amp; Download</CardTitle>
                {selectedPreset && (
                  <CardDescription>
                    {selectedPreset.platform} — {selectedPreset.label}
                    <br />
                    <span className="font-mono">
                      {selectedPreset.width} x {selectedPreset.height} px
                    </span>
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleResize}
                  disabled={!sourceImage || !selectedPreset || isProcessing}
                  className="w-full h-11"
                >
                  {isProcessing ? "Resizing..." : "Resize Image"}
                </Button>

                {resizedDataUrl && (
                  <>
                    <div className="rounded-lg overflow-hidden border bg-muted/30 flex items-center justify-center p-2">
                      <img
                        src={resizedDataUrl}
                        alt="Resized image preview"
                        className="max-h-32 max-w-full object-contain"
                      />
                    </div>
                    <Button
                      onClick={handleDownload}
                      variant="secondary"
                      className="w-full h-11 gap-2"
                    >
                      <HiDownload className="h-4 w-4" /> Download PNG
                    </Button>
                  </>
                )}

                <Button variant="outline" onClick={handleReset} className="w-full" size="sm">
                  <HiRefresh className="mr-1 h-4 w-4" /> Start Over
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Processed in your browser. Nothing is uploaded.
                </p>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-2">Downloading videos too?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                <strong>Video Downloader Pro</strong> lets you save videos from social media
                platforms directly to your device.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/video-downloader-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
